import { useEffect } from 'react';

/**
 * Per-route `document.title` hook (Phase 7 v1.1 iter 297).
 *
 * The marketing SPA shared a single `<title>` from `index.html`
 * across every route, so the browser tab always read the home-page
 * tagline — bad for tab UX (every Syncanix tab looked identical when
 * narrow), bad for SEO (search engines prefer per-page titles), and
 * actively misleading on the iter-295 404 page whose tab confidently
 * read "From git clone to live chat in 90 minutes" while the page
 * itself rendered "Page not found".
 *
 * **Empty title = no-op** (Lead Dev hat, locked): defensive guard
 * against a misconfigured page accidentally blanking the tab. If a
 * page wants to fall back to the index.html default it simply
 * omits the hook call; passing `''` to the hook is treated as a
 * no-op and tested against.
 *
 * **Restore on unmount** (Lead Dev hat, locked): the cleanup
 * function restores whatever `document.title` was when the hook
 * mounted. So mount/unmount cycles never leak a stale title into a
 * route that doesn't explicitly set one. Tested for both the
 * unmount-restore and the same-tree-rerender-replace paths.
 *
 * **Pure-function shape** (Architect hat, locked): no module-level
 * state; the hook is a thin wrapper over `useEffect` + the platform
 * `document.title` API. Trivially unit-testable in jsdom (no DOM
 * patching required).
 */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    if (title === '') return;
    const prior = document.title;
    document.title = title;
    return () => {
      document.title = prior;
    };
  }, [title]);
}

/**
 * Canonical title-suffix shape for the marketing site. Compose with
 * the page name as `${pageName} ${TITLE_SUFFIX}` for sub-pages; the
 * home page uses its own bespoke tagline-style title (see
 * `index.html` + the home page component).
 *
 * Brand "Syncanix" lands last so it shows when tabs narrow and the
 * unique page name (the actionable bit) stays visible first.
 */
export const TITLE_SUFFIX = '| Syncanix';
